import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Loader2 } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Lands here after Emergent Google Auth round-trip.
 * URL fragment looks like: /auth/callback#session_id=abc123
 * We exchange that session_id for our app JWT, store it, and continue
 * the onboarding flow (or jump to plan page if already onboarded).
 */
export default function AuthCallback() {
  const navigate = useNavigate();
  const { fetchUser } = useAuth();
  const [error, setError] = useState(null);

  useEffect(() => {
    const hash = window.location.hash || '';
    const params = new URLSearchParams(hash.startsWith('#') ? hash.slice(1) : hash);
    const sessionId = params.get('session_id');

    if (!sessionId) {
      setError('Missing session_id from Google sign-in.');
      return;
    }

    (async () => {
      try {
        const res = await axios.post(`${API}/auth/google-session`, { session_id: sessionId });
        const { access_token, user } = res.data;
        localStorage.setItem('token', access_token);
        // Pull the freshly-merged user into AuthContext
        await fetchUser();
        // Strip the session_id from the URL so it can't be re-used on refresh
        try { window.history.replaceState({}, '', '/onboarding'); } catch {}

        // If already onboarded + paid, send them home; else continue onboarding.
        const onboarded = user?.is_onboarded;
        const subscribed =
          user?.subscription_status === 'active' ||
          user?.subscription_status === 'trialing' ||
          (user?.plan && user.plan !== 'free');

        if (onboarded && subscribed) {
          navigate('/clubhouse', { replace: true });
        } else {
          navigate('/onboarding', { replace: true });
        }
      } catch (err) {
        const detail = err.response?.data?.detail || 'Sign-in failed. Please try again.';
        setError(detail);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-900 text-white px-6">
      {error ? (
        <div className="text-center max-w-md" data-testid="auth-callback-error">
          <p className="text-red-400 text-sm font-semibold mb-3">{error}</p>
          <button
            onClick={() => navigate('/onboarding', { replace: true })}
            className="px-4 py-2 bg-white text-gray-900 rounded-xl text-sm font-medium hover:bg-gray-100"
          >
            Back to sign-up
          </button>
        </div>
      ) : (
        <div className="text-center" data-testid="auth-callback-loading">
          <Loader2 className="w-8 h-8 mx-auto mb-3 animate-spin" />
          <p className="text-sm text-white/70">Signing you in...</p>
        </div>
      )}
    </div>
  );
}
